import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Container, Nav, Spinner, Alert, Badge, Button, Collapse, Modal, Form
} from 'react-bootstrap';
import {
    getEvidenceMeta,
    getEvidenceCurriculumCoverage,
    getEvidenceSourceGrounding,
    getEvidenceSLMStructure,
    getEvidenceAssessmentCoverage,     // kept for DOCX export path
    getEvidenceBloomDistribution,      // kept for DOCX export path
    getEvidenceGaps,
    saveEvidenceSnapshot,
    fillMissingSLMSections,
    getEvidenceOutcomeMapping,
    getEvidenceConceptOutcomeTraceability,
    getEvidenceRubricEvidence,
    getEvidenceGroundedAssessment,
    getEvidenceAssessmentCoverageFiltered,
    getEvidenceBloomDistributionFiltered,
    getEvidenceRubricEvidenceFiltered,
    getEvidenceGroundedAssessmentFiltered,
    getEvidenceAskOSEngagement,
    getEvidenceConceptDifficulty,
    downloadEvidenceDocx,
    getAssessmentHistory,
} from '../api/api';

// ─── Shared helpers ───────────────────────────────────────────────────────────

function SummaryCards({ cards }) {
    return (
        <div className="d-flex flex-wrap gap-3 mb-4">
            {cards.map((c, i) => (
                <div key={i} className="border rounded p-3" style={{ minWidth: 160, background: '#f8f9fa' }}>
                    <div className="text-muted small">{c.label}</div>
                    <div className="fw-bold fs-4">{c.value}</div>
                    {c.sub && <div className="text-muted" style={{ fontSize: 11 }}>{c.sub}</div>}
                </div>
            ))}
        </div>
    );
}

function StatusBadge({ value }) {
    const map = {
        covered: 'success', grounded: 'success', complete: 'success', passed: 'success',
        partial: 'warning', not_started: 'danger', not_grounded: 'danger',
        missing: 'danger', flagged: 'danger',
        pending: 'secondary', manual_edit: 'info',
    };
    return <Badge bg={map[value] || 'secondary'}>{value?.replace(/_/g, ' ')}</Badge>;
}

function BloomBadge({ level }) {
    const colours = {
        Remember: '#e3f2fd', Understand: '#e8f5e9', Apply: '#fff9c4',
        Analyze: '#fce4ec', Analyse: '#fce4ec', Evaluate: '#f3e5f5',
        Create: '#fff3e0', Synthesize: '#fff3e0', Unspecified: '#f5f5f5',
    };
    return (
        <span className="badge" style={{ background: colours[level] || '#f5f5f5', color: '#333', fontSize: 11 }}>
            {level}
        </span>
    );
}

function GuidelinesPanel({ title, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-4 border rounded" style={{ background: '#fffdf5' }}>
            <button
                className="btn btn-link text-start w-100 py-2 px-3 text-decoration-none d-flex justify-content-between align-items-center"
                style={{ color: '#6c6800', fontWeight: 600, fontSize: 13 }}
                onClick={() => setOpen(o => !o)}
            >
                <span>📘 Dashboard Guidelines — {title}</span>
                <span style={{ fontSize: 11, fontWeight: 400 }}>{open ? '▲ hide' : '▼ show'}</span>
            </button>
            <Collapse in={open}>
                <div className="px-4 pb-3 pt-1" style={{ fontSize: 13, color: '#444', lineHeight: 1.7 }}>
                    {children}
                </div>
            </Collapse>
        </div>
    );
}

function GlossaryTerm({ term, children }) {
    return (
        <div className="mb-2">
            <span className="fw-semibold">{term}:</span> {children}
        </div>
    );
}

const humanise = name => (name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());


// ─── 1. Curriculum Coverage ───────────────────────────────────────────────────

function CurriculumCoverageTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceCurriculumCoverage(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load curriculum coverage data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows } = data;
    return (
        <>
            <GuidelinesPanel title="Curriculum-to-Concept Coverage">
                <p>Shows whether every unit in the approved curriculum has been converted into a Concept Knowledge Base (CKB).</p>
                <div className="fw-semibold mb-1">Glossary</div>
                <GlossaryTerm term="Curriculum Topics">Topics listed in the unit in the uploaded syllabus structure.</GlossaryTerm>
                <GlossaryTerm term="Concepts Generated">Concept nodes created in the CKB after extraction ran.</GlossaryTerm>
                <GlossaryTerm term="Core Concepts">Concepts marked <code>is_core_definition = true</code> — foundational ideas.</GlossaryTerm>
                <GlossaryTerm term="Difficulty Range">Min / Max / Average of <code>difficulty_score</code> (1.0–5.0) across unit concepts.</GlossaryTerm>
                <div className="fw-semibold mt-2 mb-1">Coverage Status</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    <strong>covered</strong> — concepts ≥ topics &nbsp;|&nbsp;
                    <strong>partial</strong> — some concepts exist &nbsp;|&nbsp;
                    <strong>not_started</strong> — no concepts extracted
                </div>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Units', value: summary.total_units },
                { label: 'Covered', value: summary.covered },
                { label: 'Partial', value: summary.partial },
                { label: 'Not Started', value: summary.not_started },
                { label: 'Total Concepts', value: summary.total_concepts },
                { label: 'Core Concepts', value: summary.core_concepts },
            ]} />
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Module</th><th>Unit</th><th>Topics</th><th>Concepts</th><th>Core</th><th>Bloom Levels</th><th>Difficulty</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td>{r.module}</td><td>{r.unit}</td>
                                <td className="text-center">{r.curriculum_topics}</td>
                                <td className="text-center">{r.concepts_generated}</td>
                                <td className="text-center">{r.core_concepts}</td>
                                <td>{r.bloom_levels.map((bl, j) => <BloomBadge key={j} level={bl} />)}</td>
                                <td className="text-center text-muted small">
                                    {r.difficulty_range.min !== null ? `${r.difficulty_range.min}–${r.difficulty_range.max} (avg ${r.difficulty_range.avg})` : '—'}
                                </td>
                                <td><StatusBadge value={r.coverage_status} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}


// ─── 2. Source Grounding ──────────────────────────────────────────────────────

function SourceGroundingTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceSourceGrounding(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load source grounding data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows } = data;
    return (
        <>
            <GuidelinesPanel title="Source Grounding">
                <p>Confirms that each CKB concept is anchored to a specific location in an approved textbook or reference. Ungrounded concepts should be reviewed before content or assessments are published.</p>
                <div className="fw-semibold mb-1">Glossary</div>
                <GlossaryTerm term="Reference Book">Display name from <code>textbook_caches.display_name</code> — the book the concept was extracted from.</GlossaryTerm>
                <GlossaryTerm term="Book Hash">SHA-256 fingerprint of the source book file — identifies the exact version used.</GlossaryTerm>
                <GlossaryTerm term="Cache Block ID">Vertex AI context-cache block covering the relevant page range.</GlossaryTerm>
                <GlossaryTerm term="Page Range">Start and end pages in the book where supporting text was found.</GlossaryTerm>
                <GlossaryTerm term="Source Confidence">AI's self-reported confidence (0–100%) that the paragraph supports the concept.</GlossaryTerm>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Grounding Rate = grounded concepts ÷ total concepts × 100
                </div>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Concepts', value: summary.total_concepts },
                { label: 'Grounded', value: summary.grounded },
                { label: 'Not Grounded', value: summary.not_grounded },
                { label: 'Grounding Rate', value: `${summary.grounding_rate_pct}%` },
            ]} />
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Unit</th><th>Concept</th><th>Reference Book</th><th>Cache Block</th><th>Page Range</th><th>Paragraph IDs</th><th>Confidence</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const src = r.sources[0] || {};
                            return (
                                <tr key={i}>
                                    <td className="text-muted small">{r.unit_id || '—'}</td>
                                    <td>{r.concept_name}</td>
                                    <td className="small">{src.book_name || <span className="text-muted">—</span>}</td>
                                    <td className="text-center">{src.cache_block_id ?? '—'}</td>
                                    <td className="text-center small">{src.page_range ? `${src.page_range[0]}–${src.page_range[1]}` : '—'}</td>
                                    <td className="text-muted small">{src.paragraph_ids?.length ? src.paragraph_ids.slice(0, 3).join(', ') : '—'}</td>
                                    <td className="text-center">{src.source_confidence != null ? (src.source_confidence * 100).toFixed(0) + '%' : '—'}</td>
                                    <td><StatusBadge value={r.grounding_status} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}


// ─── 3. SLM Structure (dynamic + fill-gaps) ───────────────────────────────────

function FillSectionsModal({ show, onHide, row, stepNames, slug, projectId, onDone }) {
    const missing = stepNames.filter(n => !row?.flags?.[n]);
    const [selected, setSelected] = useState([]);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) { setSelected(missing); setResult(null); setError(null); }
    }, [show]); // eslint-disable-line

    const toggle = n => setSelected(s => s.includes(n) ? s.filter(x => x !== n) : [...s, n]);

    const handleGenerate = async () => {
        if (!selected.length) return;
        setRunning(true);
        try {
            const r = await fillMissingSLMSections(slug, projectId, row.content_block_id, selected);
            setResult(r.data);
            onDone();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Generation failed.');
        } finally {
            setRunning(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 15 }}>
                    Fill Missing Sections — Block {row?.content_block_id}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!result ? (
                    <>
                        <p className="text-muted small mb-3">
                            Select the missing sections to generate using DeepSeek.
                            Generated text is appended to the existing content — nothing is overwritten.
                            Word budget is calculated from the Content Standard for each section.
                        </p>
                        {missing.length === 0 ? (
                            <Alert variant="success" role="status">All sections are already present.</Alert>
                        ) : (
                            missing.map(n => (
                                <Form.Check
                                    key={n}
                                    type="checkbox"
                                    id={`chk-${n}`}
                                    label={humanise(n)}
                                    checked={selected.includes(n)}
                                    onChange={() => toggle(n)}
                                    className="mb-2"
                                />
                            ))
                        )}
                        {error && <Alert variant="danger" className="mt-2" role="alert">{error}</Alert>}
                    </>
                ) : (
                    <>
                        <Alert variant="success" role="status">
                            Generated {result.sections_generated} of {result.sections_requested} sections.
                            <strong> +{result.total_words_added} words</strong> added.
                            New total: {result.new_word_count} words.
                        </Alert>
                        {result.results.map((r, i) => (
                            <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                <Badge bg={r.status === 'generated' ? 'success' : 'danger'}>{r.status}</Badge>
                                <span className="small">{humanise(r.step)}</span>
                                {r.words_added && <span className="text-muted small">{r.words_added} words</span>}
                            </div>
                        ))}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
                {!result && (
                    <Button variant="success" size="sm" onClick={handleGenerate}
                        disabled={running || !selected.length}>
                        {running ? <><Spinner animation="border" size="sm" aria-hidden="true" /> Generating…</> : '✦ Generate'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function SLMStructureTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fillTarget, setFillTarget] = useState(null);

    const reload = useCallback(() => {
        setLoading(true);
        getEvidenceSLMStructure(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load SLM structure data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId]);

    useEffect(() => { reload(); }, [reload]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows, step_names = [] } = data;
    const tick = v => v
        ? <span style={{ color: '#198754', fontSize: 14 }}>✔</span>
        : <span style={{ color: '#dc3545', fontSize: 14 }}>✘</span>;

    return (
        <>
            <GuidelinesPanel title="SLM Structure">
                <p>Checks whether each content block contains all sections required by the project's Content Standard (<code>mandatory_elements.unit_structure</code>). Detection is dynamic — section names adapt to your Content Standard.</p>
                <div className="fw-semibold mb-1">Detection Logic</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    <strong>Introduction-family</strong> → checks <code>introduction</code> column (≥50 chars).<br />
                    <strong>Summary-family</strong> → checks <code>summary</code> column (≥50 chars).<br />
                    <strong>Conclusion-family</strong> → checks <code>conclusion</code> column (≥30 chars).<br />
                    <strong>All other sections</strong> → keyword search in full content body.<br />
                </div>
                <div className="fw-semibold mt-2 mb-1">Status</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    <strong>complete</strong> — all sections detected &nbsp;|&nbsp;
                    <strong>partial</strong> — some missing &nbsp;|&nbsp;
                    <strong>missing</strong> — no sections found
                </div>
                <div className="fw-semibold mt-2 mb-1">Fill Missing Sections</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    Click <strong>✦ Fill</strong> on any partial/missing block to generate the missing sections
                    using <strong>DeepSeek AI</strong>. Generated text is appended — existing content is never touched.
                    Token usage is billed to the tenant wallet.
                </div>
                {step_names.length > 0 && (
                    <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                        Sections checked ({step_names.length}): {step_names.map(humanise).join(' · ')}
                    </div>
                )}
            </GuidelinesPanel>

            <SummaryCards cards={[
                { label: 'Total Blocks', value: summary.total_blocks },
                { label: 'Complete', value: summary.complete },
                { label: 'Partial', value: summary.partial },
                { label: 'Missing', value: summary.missing },
                { label: 'Completion Rate', value: `${summary.completion_rate_pct}%` },
            ]} />
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle text-center">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="text-start">Unit</th>
                            <th>Block</th>
                            <th>Words</th>
                            {step_names.map(n => (
                                <th scope="col" key={n} title={n} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{humanise(n)}</th>
                            ))}
                            <th>Review</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const hasMissing = step_names.some(n => !r.flags?.[n]);
                            return (
                                <tr key={i}>
                                    <td className="text-muted small text-start">{r.unit_id || '—'}</td>
                                    <td>{r.content_block_id}</td>
                                    <td>{r.word_count ?? '—'}</td>
                                    {step_names.map(n => <td key={n}>{tick(r.flags?.[n])}</td>)}
                                    <td><Badge bg="secondary" style={{ fontSize: 10 }}>{r.review_status}</Badge></td>
                                    <td><StatusBadge value={r.structure_status} /></td>
                                    <td>
                                        {hasMissing && (
                                            <Button variant="outline-success" size="sm" style={{ fontSize: 11 }}
                                                onClick={() => setFillTarget(r)}>
                                                ✦ Fill
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {fillTarget && (
                <FillSectionsModal
                    show={!!fillTarget}
                    onHide={() => setFillTarget(null)}
                    row={fillTarget}
                    stepNames={step_names}
                    slug={slug}
                    projectId={projectId}
                    onDone={() => { setFillTarget(null); reload(); }}
                />
            )}
        </>
    );
}


// ─── 4. Assessment Coverage ───────────────────────────────────────────────────

function AssessmentCoverageTab({ slug, projectId, blueprintId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceAssessmentCoverageFiltered(slug, projectId, blueprintId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load assessment coverage data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId, blueprintId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows } = data;
    const tick = v => v
        ? <span style={{ color: '#198754' }}>✔</span>
        : <span style={{ color: '#dc3545' }}>✘</span>;

    return (
        <>
            <GuidelinesPanel title="Assessment Coverage">
                <p>Shows question coverage by CO, Bloom level, difficulty, rubric presence, and source grounding.</p>
                <div className="fw-semibold mb-1">Glossary</div>
                <GlossaryTerm term="CO">Course Outcome mapped to this question (<code>assessment_bank.co_mapping</code>).</GlossaryTerm>
                <GlossaryTerm term="Rubric">Structured marking guide required for Essay, Short Answer, Case Study questions.</GlossaryTerm>
                <GlossaryTerm term="QC Status"><strong>passed</strong> auto-validated · <strong>flagged</strong> needs review · <strong>pending</strong> unchecked · <strong>manual_edit</strong> modified post-QC.</GlossaryTerm>
                <GlossaryTerm term="Grounding">Question linked to source-grounded concept via <code>grounding_ref.source_chunk_id</code>.</GlossaryTerm>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Questions', value: summary.total_questions },
                { label: 'With Rubric', value: summary.with_rubric },
                { label: 'With Answer Key', value: summary.with_answer_key },
                { label: 'With Explanation', value: summary.with_explanation },
                { label: 'Grounded', value: summary.with_grounding },
                { label: 'QC Passed', value: summary.qc_passed },
                { label: 'QC Flagged', value: summary.qc_flagged },
            ]} />
            {Object.keys(summary.questions_per_co).length > 0 && (
                <div className="mb-3">
                    <strong className="small text-muted">Questions per Course Outcome</strong>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                        {Object.entries(summary.questions_per_co).map(([co, n]) => (
                            <Badge key={co} bg="info" text="dark">{co}: {n}</Badge>
                        ))}
                    </div>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle text-center">
                    <thead className="table-light">
                        <tr><th>Q ID</th><th>Type</th><th>CO</th><th>Bloom</th><th>Difficulty (1–5)</th><th>Rubric</th><th>Answer Key</th><th>Explanation</th><th>QC Status</th><th>Grounding</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td>{r.question_id}</td>
                                <td><Badge bg="secondary" style={{ fontSize: 10 }}>{r.type}</Badge></td>
                                <td>{r.co || '—'}</td>
                                <td>{r.bloom_level ? <BloomBadge level={r.bloom_level} /> : '—'}</td>
                                <td>{r.difficulty_score != null ? `${r.difficulty_score}/5` : '—'}</td>
                                <td>{tick(r.rubric_present)}</td>
                                <td>{tick(r.answer_key_present)}</td>
                                <td>{tick(r.explanation_present)}</td>
                                <td><StatusBadge value={r.qc_status} /></td>
                                <td>{tick(r.grounding_present)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}


// ─── 5. Bloom Distribution ────────────────────────────────────────────────────

function BloomDistributionTab({ slug, projectId, blueprintId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceBloomDistributionFiltered(slug, projectId, blueprintId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load Bloom distribution data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId, blueprintId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows } = data;
    const COLOURS = {
        Remember: '#90caf9', Understand: '#a5d6a7', Apply: '#fff176',
        Analyze: '#ef9a9a', Analyse: '#ef9a9a', Evaluate: '#ce93d8',
        Create: '#ffcc80', Synthesize: '#ffcc80', Unspecified: '#e0e0e0',
    };

    const DistBar = ({ dist }) => {
        const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
        return (
            <div className="d-flex" style={{ height: 16, borderRadius: 4, overflow: 'hidden', minWidth: 120 }}>
                {Object.entries(dist).map(([bl, n]) => (
                    <div key={bl} title={`${bl}: ${n}`}
                        style={{ width: `${(n / total * 100).toFixed(1)}%`, background: COLOURS[bl] || '#e0e0e0' }} />
                ))}
            </div>
        );
    };

    return (
        <>
            <GuidelinesPanel title="Bloom & Cognitive Distribution">
                <p>Compares cognitive levels of CKB concepts against assessment questions per unit. A well-designed course has assessments at a similar or slightly higher level than taught concepts.</p>
                <div className="fw-semibold mb-1">Glossary</div>
                <GlossaryTerm term="Concept HO%">Higher-order concepts (L3–L6: Analyze, Evaluate, Create) ÷ total concepts × 100.</GlossaryTerm>
                <GlossaryTerm term="Assessment HO%">Higher-order questions (L3–L6) ÷ total questions × 100.</GlossaryTerm>
                <GlossaryTerm term="Gap">|Concept HO% − Assessment HO%|. Flagged amber when &gt;20%.</GlossaryTerm>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    Questions are assigned to a unit via their first <code>concept_mapping</code> entry. Questions with no mapping appear under "unassigned".
                </div>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Concepts', value: summary.total_concepts },
                { label: 'Total Questions', value: summary.total_questions },
                { label: 'Concept HO%', value: `${summary.concept_higher_order_pct}%`, sub: 'L3–L6' },
                { label: 'Assessment HO%', value: `${summary.assessment_higher_order_pct}%`, sub: 'L3–L6' },
                { label: 'Units with Gap >20%', value: summary.units_with_gap },
            ]} />
            <div className="mb-3">
                <strong className="small text-muted me-2">Overall concept distribution</strong>
                <DistBar dist={summary.concept_bloom_totals} />
            </div>
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Unit</th><th>Concept Bloom</th><th>Assessment Bloom</th><th>Concept HO%</th><th>Assessment HO%</th><th>Gap</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className={r.gap_flag ? 'table-warning' : ''}>
                                <td className="text-muted small">{r.unit_id}</td>
                                <td><DistBar dist={r.concept_bloom_distribution} /></td>
                                <td><DistBar dist={r.assessment_bloom_distribution} /></td>
                                <td className="text-center">{r.concept_higher_order_pct}%</td>
                                <td className="text-center">{r.assessment_higher_order_pct}%</td>
                                <td className="text-center">
                                    {r.gap_flag ? <Badge bg="danger">{r.gap_indicator}%</Badge>
                                               : <span className="text-muted small">{r.gap_indicator}%</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}


// ─── 6. Gap Analysis ─────────────────────────────────────────────────────────

function GapAnalysisTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback((refresh = false) => {
        setLoading(true);
        if (refresh) setRefreshing(true);
        getEvidenceGaps(slug, projectId, refresh)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load gap analysis data.'))
            .finally(() => { setLoading(false); setRefreshing(false); });
    }, [slug, projectId]);

    useEffect(() => { load(false); }, [load]);

    if (loading && !refreshing) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error) return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data) return null;

    const { summary, rows } = data;
    const sevColour = { high: 'danger', medium: 'warning', low: 'secondary' };
    const typeLabel = {
        curriculum: 'Curriculum', source_grounding: 'Source Grounding',
        slm_structure: 'SLM Content', assessment_coverage: 'Assessment',
        rubric_evidence: 'Rubric', assessment_qc: 'QC',
    };

    return (
        <>
            <GuidelinesPanel title="Gap Analysis">
                <p>Consolidates actionable gaps from all evidence dimensions. Does not assign a compliance score — for internal review only.</p>
                <div className="fw-semibold mb-1">Gap Categories</div>
                <GlossaryTerm term="Curriculum">Units with no topics, or no CKB extraction.</GlossaryTerm>
                <GlossaryTerm term="Source Grounding">Concepts not anchored to a textbook location.</GlossaryTerm>
                <GlossaryTerm term="SLM Content">Content blocks with missing required sections.</GlossaryTerm>
                <GlossaryTerm term="Assessment">Concepts with no questions in the bank.</GlossaryTerm>
                <GlossaryTerm term="Rubric">Subjective questions without a marking rubric.</GlossaryTerm>
                <GlossaryTerm term="QC">Questions that failed automated quality control.</GlossaryTerm>
                <div className="fw-semibold mt-2 mb-1">Severity</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                    <strong>High</strong> — blocks production or student access &nbsp;|&nbsp;
                    <strong>Medium</strong> — reduces quality &nbsp;|&nbsp;
                    <strong>Low</strong> — advisory
                </div>
            </GuidelinesPanel>

            <div className="d-flex justify-content-between align-items-start mb-3">
                <SummaryCards cards={[
                    { label: 'Total Gaps', value: summary.total_gaps },
                    { label: 'High', value: summary.by_severity.high },
                    { label: 'Medium', value: summary.by_severity.medium },
                    { label: 'Low', value: summary.by_severity.low },
                ]} />
                <Button variant="outline-secondary" size="sm" onClick={() => load(true)} disabled={refreshing}>
                    {refreshing ? <Spinner animation="border" size="sm" aria-hidden="true" /> : '↻ Re-derive Gaps'}
                </Button>
            </div>

            {Object.keys(summary.by_type).length > 0 && (
                <div className="mb-3 d-flex flex-wrap gap-2">
                    {Object.entries(summary.by_type).map(([t, n]) => (
                        <Badge key={t} bg="light" text="dark" className="border">{typeLabel[t] || t}: {n}</Badge>
                    ))}
                </div>
            )}

            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Severity</th><th>Category</th><th>Unit</th><th>CO</th><th>Issue</th><th>Suggested Action</th></tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted py-4">No gaps detected.</td></tr>
                        )}
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td><Badge bg={sevColour[r.severity] || 'secondary'}>{r.severity}</Badge></td>
                                <td><span className="text-muted small">{typeLabel[r.gap_type] || r.gap_type}</span></td>
                                <td className="text-muted small">{r.unit_id || '—'}</td>
                                <td className="text-muted small">{r.co_code || '—'}</td>
                                <td style={{ maxWidth: 320 }}>{r.issue}</td>
                                <td style={{ maxWidth: 260 }} className="text-muted small">{r.suggested_action}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {data.cached && <div className="text-muted small mt-1">Showing cached gaps — click Re-derive to refresh.</div>}
        </>
    );
}


// ─── V2 generic table tab ────────────────────────────────────────────────────
// Renders any V2 dashboard that returns a flat rows[] array.
// columns is the ordered list of row keys to display as table columns.

function V2SimpleTab({ slug, projectId, dashKey, fetchFn, columns, title }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        fetchFn(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError(`Failed to load ${title} data.`))
            .finally(() => setLoading(false));
    }, [slug, projectId]); // eslint-disable-line

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error)   return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data)   return null;

    const { summary, rows } = data;
    const colLabel = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const formatVal = (v) => {
        if (v === null || v === undefined) return '—';
        if (typeof v === 'boolean') return v ? '✔' : '✘';
        if (Array.isArray(v)) return v.join(', ') || '—';
        return String(v);
    };

    return (
        <>
            <SummaryCards cards={Object.entries(summary)
                .filter(([, v]) => typeof v !== 'object')
                .map(([k, v]) => ({ label: colLabel(k), value: v }))} />
            {rows.length === 0
                ? <Alert variant="info" role="status">No data available. {dashKey === 'outcome-mapping' ? 'Add outcome mappings to see this dashboard.' : ''}</Alert>
                : (
                <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                        <thead className="table-light">
                            <tr>{columns.map(c => <th scope="col" key={c} style={{ fontSize: 12 }}>{colLabel(c)}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i}>
                                    {columns.map(c => (
                                        <td key={c} style={{ fontSize: 12 }}>{formatVal(r[c])}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

// ─── AskOS Engagement tab (V2 #11) ───────────────────────────────────────────

function AskOSEngagementTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceAskOSEngagement(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load AskOS engagement data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error)   return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data)   return null;

    const { summary, rows, concept_aggregates = [] } = data;

    return (
        <>
            <GuidelinesPanel title="Student AskOS Engagement">
                <p>Shows how students are interacting with the AI professor per concept. Use this to identify which topics generate the most doubt-clearing activity.</p>
                <GlossaryTerm term="Questions Asked">Total user messages in the session's chat history.</GlossaryTerm>
                <GlossaryTerm term="Voice Used">Whether the student had voice (TTS) enabled during the session.</GlossaryTerm>
                <GlossaryTerm term="Concept Aggregates">Session and question counts rolled up per concept — useful for identifying hotspot topics.</GlossaryTerm>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Sessions', value: summary.total_sessions },
                { label: 'Unique Students', value: summary.unique_students },
                { label: 'Unique Concepts', value: summary.unique_concepts },
                { label: 'Total Questions', value: summary.total_questions },
                { label: 'Voice Sessions', value: summary.voice_sessions },
            ]} />

            {concept_aggregates.length > 0 && (
                <div className="mb-4">
                    <strong className="small text-muted">Top concepts by student sessions</strong>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                        {concept_aggregates.slice(0, 8).map((c, i) => (
                            <Badge key={i} bg="info" text="dark" style={{ fontSize: 11 }}>
                                {c.concept_name}: {c.sessions} sessions / {c.questions} Qs
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Student</th><th>Concept</th><th>Questions</th><th>Language</th><th>Voice</th><th>Last Interaction</th></tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No student sessions recorded yet.</td></tr>}
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td className="small">{r.student_email}</td>
                                <td>{r.concept_name}</td>
                                <td className="text-center">{r.questions_asked}</td>
                                <td className="text-muted small">{r.language}</td>
                                <td className="text-center">{r.voice_used ? '✔' : '—'}</td>
                                <td className="text-muted small">{r.last_interaction ? r.last_interaction.slice(0, 16) : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

// ─── Concept Difficulty tab (V2 #12) ─────────────────────────────────────────

function ConceptDifficultyTab({ slug, projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        getEvidenceConceptDifficulty(slug, projectId)
            .then(r => setData(r.data))
            .catch(() => setError('Failed to load concept difficulty data.'))
            .finally(() => setLoading(false));
    }, [slug, projectId]);

    if (loading) return <Spinner animation="border" size="sm" className="m-4" aria-hidden="true" />;
    if (error)   return <Alert variant="danger" role="alert">{error}</Alert>;
    if (!data)   return null;

    const { summary, rows } = data;
    const priorityColour = { high: 'danger', medium: 'warning', low: 'secondary' };

    return (
        <>
            <GuidelinesPanel title="Concept Difficulty & Student Confusion">
                <p>Ranks concepts by student confusion signal. Concepts with many sessions and repeated doubts surface at the top and are candidates for SLM improvement or faculty intervention.</p>
                <GlossaryTerm term="Difficulty Score">CKB-assigned difficulty (1.0 easy → 5.0 hardest).</GlossaryTerm>
                <GlossaryTerm term="Student Sessions">Number of AskOS sessions where students asked about this concept.</GlossaryTerm>
                <GlossaryTerm term="Repeated Doubts">Sessions where the student asked more than 3 questions — indicates the concept was not resolved quickly.</GlossaryTerm>
                <GlossaryTerm term="Support Priority">High = &gt;5 sessions OR repeated doubts. Medium = 1-5 sessions. Low = no sessions yet.</GlossaryTerm>
            </GuidelinesPanel>
            <SummaryCards cards={[
                { label: 'Total Concepts', value: summary.total_concepts },
                { label: 'High Priority', value: summary.high_priority },
                { label: 'Medium Priority', value: summary.medium_priority },
                { label: 'Unvisited', value: summary.unvisited_concepts },
                { label: 'Avg Q/Session', value: summary.avg_questions_per_session },
            ]} />
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                        <tr><th>Concept</th><th>Unit</th><th>Bloom</th><th>Difficulty</th><th>Sessions</th><th>Questions</th><th>Repeated Doubts</th><th>Priority</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className={r.support_priority === 'high' ? 'table-danger' : r.support_priority === 'medium' ? 'table-warning' : ''}>
                                <td>{r.concept_name}</td>
                                <td className="text-muted small">{r.unit_id || '—'}</td>
                                <td><BloomBadge level={r.bloom_level} /></td>
                                <td className="text-center">{r.difficulty_score ?? '—'}</td>
                                <td className="text-center">{r.student_sessions}</td>
                                <td className="text-center">{r.total_questions}</td>
                                <td className="text-center">{r.repeated_doubts}</td>
                                <td><Badge bg={priorityColour[r.support_priority] || 'secondary'}>{r.support_priority}</Badge></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// V1 tabs — all studio roles
const V1_TABS = [
    { key: 'curriculum-coverage', label: 'Curriculum Coverage' },
    { key: 'source-grounding',    label: 'Source Grounding' },
    { key: 'slm-structure',       label: 'SLM Structure' },
    { key: 'assessment-coverage', label: 'Assessment Coverage' },
    { key: 'bloom-distribution',  label: 'Bloom Distribution' },
    { key: 'gaps',                label: 'Gap Analysis' },
];

// Outcome tabs — available to ALL studio roles (backend now uses _require_studio)
const OUTCOME_TABS = [
    { key: 'outcome-mapping',              label: 'Outcome Mapping' },
    { key: 'concept-outcome-traceability', label: 'Concept-Outcome Traceability' },
];

// V2 tabs — tenant_admin only
const V2_ADMIN_TABS = [
    { key: 'rubric-evidence',    label: 'Rubric Evidence' },
    { key: 'grounded-assessment',label: 'Grounded Assessment' },
    { key: 'askos-engagement',   label: 'AskOS Engagement' },
    { key: 'concept-difficulty', label: 'Concept Difficulty' },
];

export default function AcademicEvidenceDashboards() {
    const { slug, projectId } = useParams();
    const navigate  = useNavigate();
    const { user }  = useSelector((state) => state.user || {});
    const isAdmin   = user?.role === 'tenant_admin' || user?.role === 'system_admin';
    // All studio roles see V1 + Outcome tabs; admins additionally see V2 admin-only tabs
    const TABS = isAdmin
        ? [...V1_TABS, ...OUTCOME_TABS, ...V2_ADMIN_TABS]
        : [...V1_TABS, ...OUTCOME_TABS];
    const [activeTab,    setActiveTab]    = useState('curriculum-coverage');
    const [meta,             setMeta]             = useState(null);
    const [saving,           setSaving]           = useState(false);
    const [saveMsg,          setSaveMsg]          = useState(null);
    const [downloading,      setDownloading]      = useState(false);
    const [blueprints,       setBlueprints]       = useState([]);
    const [selectedBpId,     setSelectedBpId]     = useState(null); // null = all banks

    useEffect(() => {
        getEvidenceMeta(slug, projectId)
            .then(r => setMeta(r.data))
            .catch(() => {});
        // Load available assessment banks for the blueprint selector
        getAssessmentHistory(slug, projectId)
            .then(r => {
                const bps = Array.isArray(r.data) ? r.data : [];
                setBlueprints(bps);
                // Default to the most recent bank
                if (bps.length > 0 && !selectedBpId) {
                    setSelectedBpId(bps[0].id);
                }
            })
            .catch(() => {});
    }, [slug, projectId]); // eslint-disable-line

    const handleSaveSnapshot = useCallback(async () => {
        setSaving(true); setSaveMsg(null);
        try {
            await saveEvidenceSnapshot(slug, projectId, activeTab, { note: 'Manual snapshot' });
            setSaveMsg({ type: 'success', text: 'Snapshot saved.' });
        } catch {
            setSaveMsg({ type: 'danger', text: 'Save failed.' });
        } finally {
            setSaving(false);
            setTimeout(() => setSaveMsg(null), 4000);
        }
    }, [slug, projectId, activeTab]);

    const handleDownloadDocx = useCallback(async () => {
        setDownloading(true);
        try {
            const response = await downloadEvidenceDocx(slug, projectId, activeTab);
            const url  = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', `evidence_${activeTab}_${projectId}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('DOCX download failed', e);
        } finally {
            setDownloading(false);
        }
    }, [slug, projectId, activeTab]);

    const titleLine = meta
        ? `${meta.project_name}${meta.program_name ? ` · ${meta.program_name}` : ''}`
        : 'Academic Evidence Dashboards';

    return (
        <Container fluid className="py-4 px-4">
            {/* ── Title bar ── */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <button
                        className="btn btn-sm btn-link text-muted p-0 mb-1"
                        onClick={() => navigate(`/tenant/${slug}/dashboard`)}
                    >
                        ← Back to Dashboard
                    </button>
                    <h4 className="mb-0 fw-bold">{titleLine}</h4>
                    {meta?.content_standard_name && (
                        <div className="text-muted small">
                            Content Standard: <strong>{meta.content_standard_name}</strong>
                        </div>
                    )}
                    <p className="text-muted small mb-0">Academic Evidence Dashboards</p>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {saveMsg && (
                        <Alert variant={saveMsg.type} className="py-1 px-3 mb-0 small" role="status">{saveMsg.text}</Alert>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={handleDownloadDocx} disabled={downloading}>
                        {downloading ? <Spinner animation="border" size="sm" aria-hidden="true" /> : '⬇ Export DOCX'}
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={handleSaveSnapshot} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : '↓ Save Snapshot'}
                    </Button>
                </div>
            </div>

            {/* ── Tab nav ── */}
            <Nav variant="tabs" className="mb-3 border-bottom" activeKey={activeTab} onSelect={setActiveTab}>
                {TABS.map(t => (
                    <Nav.Item key={t.key}>
                        <Nav.Link eventKey={t.key} style={{ fontSize: 13 }}>{t.label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {/* ── Blueprint selector (assessment tabs only) ── */}
            {['assessment-coverage','bloom-distribution','rubric-evidence','grounded-assessment'].includes(activeTab) && (
                <div className="d-flex align-items-center gap-2 mb-3 p-2 border rounded bg-white">
                    <span className="text-muted small fw-semibold" style={{ whiteSpace: 'nowrap' }}>Assessment Bank:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ maxWidth: 340 }}
                        value={selectedBpId ?? ''}
                        onChange={e => setSelectedBpId(e.target.value ? Number(e.target.value) : null)}
                    >
                        <option value="">All banks (combined)</option>
                        {blueprints.map(bp => (
                            <option key={bp.id} value={bp.id}>
                                {bp.blueprint_name || `Bank #${bp.id}`}
                                {bp.total_questions ? ` — ${bp.total_questions} questions` : ''}
                                {bp.created_at ? ` (${bp.created_at.slice(0,10)})` : ''}
                            </option>
                        ))}
                    </select>
                    {blueprints.length === 0 && (
                        <span className="text-muted small">No assessment banks found for this project.</span>
                    )}
                </div>
            )}

            {/* ── Tab content ── */}
            <div>

                {activeTab === 'curriculum-coverage' && <CurriculumCoverageTab slug={slug} projectId={projectId} />}
                {activeTab === 'source-grounding'    && <SourceGroundingTab    slug={slug} projectId={projectId} />}
                {activeTab === 'slm-structure'       && <SLMStructureTab       slug={slug} projectId={projectId} />}
                {/* Assessment tabs receive blueprintId so they scope to one bank */}
                {activeTab === 'assessment-coverage' && <AssessmentCoverageTab slug={slug} projectId={projectId} blueprintId={selectedBpId} />}
                {activeTab === 'bloom-distribution'  && <BloomDistributionTab  slug={slug} projectId={projectId} blueprintId={selectedBpId} />}
                {activeTab === 'gaps'                && <GapAnalysisTab        slug={slug} projectId={projectId} />}
                {/* ── V2 Dashboards (tenant_admin only) ── */}
                {activeTab === 'outcome-mapping'              && <V2SimpleTab slug={slug} projectId={projectId} dashKey="outcome-mapping"              fetchFn={getEvidenceOutcomeMapping}              columns={['unit_id','lo_code','co_code','po_code','pso_code','mapping_strength','review_status','rationale']}           title="Outcome Mapping" />}
                {activeTab === 'concept-outcome-traceability' && <V2SimpleTab slug={slug} projectId={projectId} dashKey="concept-outcome-traceability" fetchFn={getEvidenceConceptOutcomeTraceability} columns={['unit_id','concept_name','bloom_level','difficulty_score','lo_codes','co_codes','po_pso','mapping_strength','mapped']} title="Concept-to-Outcome Traceability" />}
                {activeTab === 'rubric-evidence'              && <V2SimpleTab slug={slug} projectId={projectId} dashKey="rubric-evidence"              fetchFn={(s,p) => getEvidenceRubricEvidenceFiltered(s, p, selectedBpId)}         columns={['question_id','type','co','bloom_level','difficulty_score','rubric_present','rubric_criteria_count','total_marks','qc_status']} title="Rubric & Evaluation Evidence" />}
                {activeTab === 'grounded-assessment'          && <V2SimpleTab slug={slug} projectId={projectId} dashKey="grounded-assessment"          fetchFn={(s,p) => getEvidenceGroundedAssessmentFiltered(s, p, selectedBpId)} columns={['question_id','type','co','concept_name','grounding_mode','source_pages','confidence','evidence_status']}         title="Grounded Assessment Evidence" />}
                {activeTab === 'askos-engagement'             && <AskOSEngagementTab  slug={slug} projectId={projectId} />}
                {activeTab === 'concept-difficulty'           && <ConceptDifficultyTab slug={slug} projectId={projectId} />}
            </div>
        </Container>
    );
}
